import { motion } from "framer-motion";
import {
  ArrowRight, Zap, Users, CreditCard, BarChart3,
  Bell, Shield, CheckCircle2, Sparkles, Target,
  TrendingUp, Award, Play, Mail,
  BookOpen, GraduationCap, Video, Settings,
  Heart, CheckCircle, XCircle
} from "lucide-react";

/* ─── Design tokens — Academy purple system with responsive spacing ─── */
const P = "rgb(98,92,160)";
const PD = "rgb(72,67,120)";
const PL = "rgb(130,124,196)";
const PLL = "rgb(190,185,230)";

const T = {
  navy:    "#0f172a",
  navy2:   "#1e1535",
  purp:    P,
  purpD:   PD,
  purpL:   PL,
  purpLL:  PLL,
  slate:   "#64748b",
  border:  "#e8e6f0",
  bg:      "#f8f7fc",
  white:   "#ffffff",
  green:   "#22c55e",
  greenBg: "#f0fdf4",
  greenBd: "#d1fae5",
  red:     "#ef4444",
};

/* ─── Responsive container utility ─── */
const Container = ({ children, className = "", style = {} }) => (
  <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`} style={style}>
    {children}
  </div>
);

/* ─── Responsive grid utility ─── */
const Grid = ({ children, cols = { default: 1, sm: 2, md: 3, lg: 4 }, gap = 4, className = "" }) => {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  };
  
  const gapSizes = {
    2: "gap-2",
    4: "gap-4",
    6: "gap-6",
    8: "gap-8",
  };
  
  return (
    <div className={`grid ${gridCols[cols.lg || cols.md || cols.sm || cols.default]} ${gapSizes[gap] || `gap-${gap}`} ${className}`}>
      {children}
    </div>
  );
};

/* ─── Responsive Typography ─── */
const Heading = ({ level = 1, children, gradient = false, className = "", style = {} }) => {
  const baseStyles = "font-black tracking-tight leading-tight";
  const sizes = {
    1: "text-4xl sm:text-5xl lg:text-6xl",
    2: "text-3xl sm:text-4xl lg:text-5xl",
    3: "text-2xl sm:text-3xl lg:text-4xl",
    4: "text-xl sm:text-2xl lg:text-3xl",
  };
  
  const gradientStyle = gradient ? {
    background: `linear-gradient(135deg, ${PL}, ${PLL})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  } : { color: T.navy };
  
  return (
    <div className={`${baseStyles} ${sizes[level]} ${className}`} style={{ ...gradientStyle, ...style }}>
      {children}
    </div>
  );
};

/* ─── Shared Badge (responsive) ─── */
const Badge = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-purple-50/90 border border-purple-200/50 px-3 sm:px-5 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
    {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: P }} />}
    <span className="text-xs sm:text-sm font-bold tracking-wider" style={{ color: P }}>{label}</span>
  </div>
);

/* ══════════════════════════
   HERO (fully responsive)
══════════════════════════ */
const Hero = () => (
  <section className="relative min-h-screen flex items-center bg-[#06040f] overflow-hidden px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32">
    {/* Grid Background */}
    <div className="absolute inset-0" style={{ 
      backgroundImage: "linear-gradient(rgba(98,92,160,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(98,92,160,0.06) 1px, transparent 1px)",
      backgroundSize: "clamp(30px, 6vw, 60px) clamp(30px, 6vw, 60px)"
    }} />
    
    {/* Glows */}
    <div className="absolute -top-[20%] -left-[5%] w-[70%] sm:w-[50%] h-[60%] sm:h-[70%] pointer-events-none"
         style={{ background: `radial-gradient(ellipse, rgba(98,92,160,0.18) 0%, transparent 65%)` }} />
    <div className="absolute -bottom-[10%] -right-[5%] w-[65%] sm:w-[45%] h-[50%] sm:h-[60%] pointer-events-none"
         style={{ background: `radial-gradient(ellipse, rgba(98,92,160,0.1) 0%, transparent 65%)` }} />

    <Container>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Left Content */}
        <motion.div 
          initial={{ opacity: 0, y: 28 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.7 }}
          className="text-center lg:text-left"
        >
          <div className="inline-flex items-center gap-2 bg-purple-900/20 border border-purple-500/30 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-6 sm:mb-8">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-xs sm:text-sm font-bold tracking-wider" style={{ color: PL }}>EDUCATION & COURSE DELIVERY PLATFORM</span>
          </div>

          <Heading level={1} className="text-white mb-4 sm:mb-6">
            Run Your Academy<br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-purple-300 to-purple-200 bg-clip-text text-transparent"> on Autopilot.</span>
          </Heading>

          <p className="text-base sm:text-lg text-white/60 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-8">
            Krumago Academy is the all-in-one management platform built for educators and training providers. Deliver courses, manage students, automate billing, and grow your institution — without the admin chaos.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-8 sm:mb-10">
            <motion.button 
              whileHover={{ scale: 1.02, y: -2 }} 
              whileTap={{ scale: 0.98 }}
              className="bg-purple-600 text-white border-none px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold cursor-pointer inline-flex items-center justify-center gap-2 shadow-lg shadow-purple-600/30 hover:shadow-xl transition-all"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.02, y: -2 }} 
              whileTap={{ scale: 0.98 }}
              className="bg-white/5 text-white border border-white/20 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold cursor-pointer inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
            >
              <Play className="w-4 h-4" /> Watch Demo
            </motion.button>
          </div>

          {/* Feature Pills - Responsive grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg mx-auto lg:mx-0">
            {[
              { icon: BookOpen, label: "Course Builder" },
              { icon: CreditCard, label: "Automated Billing" },
              { icon: BarChart3, label: "Learning Analytics" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 sm:gap-3 bg-white/5 rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-purple-500/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: PL }} />
                </div>
                <span className="text-xs sm:text-sm text-white/60 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right - Dashboard Mockup (fully responsive) */}
        <motion.div 
          initial={{ opacity: 0, x: 36 }} 
          animate={{ opacity: 1, x: 0 }} 
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-8 lg:mt-0"
        >
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            {/* Chrome */}
            <div className="flex items-center gap-2 mb-4 sm:mb-5">
              {["#ef4444","#f59e0b","#22c55e"].map(c => <div key={c} className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full" style={{ background: c }} />)}
              <div className="flex-1 ml-2 h-5 sm:h-6 rounded-md bg-white/5" />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
              {[["—","Students Enrolled","#a78bfa"], ["—","Courses Active","#60a5fa"], ["—%","Completion Rate","#34d399"]].map(([v,l,c]) => (
                <div key={l} className="bg-white/5 rounded-lg p-2 sm:p-4">
                  <div className="text-base sm:text-xl font-black mb-1" style={{ color: c }}>{v}</div>
                  <div className="text-[10px] sm:text-xs text-white/40">{l}</div>
                </div>
              ))}
            </div>

            {/* Course List */}
            <div className="bg-white/5 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <div className="text-[10px] sm:text-xs font-bold text-white/40 tracking-wider mb-2 sm:mb-3">LIVE COURSE STATUS</div>
              {[
                ["Web Dev","In Progress","#a78bfa"],
                ["Data Science","Enrolling","#34d399"],
                ["UI/UX","Scheduled","#60a5fa"],
                ["Cloud","Available","#34d399"],
              ].map(([name,status,color]) => (
                <div key={name} className="flex items-center justify-between mb-1.5 sm:mb-2.5 last:mb-0">
                  <span className="text-xs sm:text-sm text-white/70">{name}</span>
                  <span className="text-[9px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full" style={{ background: `${color}20`, color }}>{status}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-purple-600 text-white rounded-lg py-2 sm:py-3 text-center text-xs sm:text-sm font-bold cursor-pointer hover:bg-purple-700 transition-colors">
                + New Course
              </div>
              <div className="bg-white/5 text-white/60 border border-white/10 rounded-lg py-2 sm:py-3 text-center text-xs sm:text-sm font-bold cursor-pointer hover:bg-white/10 transition-colors">
                View Reports
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Container>
  </section>
);

/* ══════════════════════════
   MARQUEE (responsive)
══════════════════════════ */
const Marquee = () => {
  const items = [
    { icon: BookOpen,      label: "Course Builder" },
    { icon: Video,         label: "Live Class" },
    { icon: CreditCard,    label: "Auto Billing" },
    { icon: GraduationCap, label: "Certificates" },
    { icon: BarChart3,     label: "Analytics" },
    { icon: Users,         label: "Student CRM" },
    { icon: Bell,          label: "Notifications" },
    { icon: Shield,        label: "Assessments" },
  ];
  
  return (
    <div className="bg-[#1e1535] border-y border-white/10 py-3 sm:py-4 overflow-hidden">
      <motion.div 
        animate={{ x: ["0%", "-50%"] }} 
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="flex gap-8 sm:gap-14 whitespace-nowrap"
      >
        {[...items, ...items].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="inline-flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: PL }} />
              <span className="text-xs sm:text-sm text-white/70 font-semibold">{item.label}</span>
              <span className="text-white/20 ml-2 sm:ml-4">·</span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

/* ══════════════════════════
   MISSION (responsive)
══════════════════════════ */
const Mission = () => (
  <section className="bg-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
    <Container>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Visual - Responsive */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }} 
          whileInView={{ opacity: 1, x: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.65 }}
          className="order-2 lg:order-1 mt-8 lg:mt-0"
        >
          <div className="relative">
            <div className="bg-gradient-to-br from-slate-900 to-[#1e1535] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
              <div className="text-[10px] sm:text-xs font-bold tracking-wider mb-4 sm:mb-6" style={{ color: PL }}>ACADEMY ANALYTICS DASHBOARD</div>

              {/* Mini bar chart - responsive heights */}
              <div className="flex items-end gap-1 sm:gap-1.5 h-16 sm:h-20 mb-4 sm:mb-5">
                {[38,55,42,70,50,80,65,88,60,92,75,100].map((h, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ height: 0 }} 
                    whileInView={{ height: `${h}%` }} 
                    viewport={{ once: true }} 
                    transition={{ delay: i * 0.05, duration: 0.5 }}
                    className="flex-1 rounded-sm"
                    style={{ background: i === 11 ? P : "rgba(255,255,255,0.1)" }}
                  />
                ))}
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-0">
                <div>
                  <div className="text-white/40 text-xs sm:text-sm">Course Completions</div>
                  <div className="text-white text-xl sm:text-2xl lg:text-3xl font-black">Your Data</div>
                  <div className="text-green-500 text-xs sm:text-sm font-semibold">↑ Growing enrolment</div>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-white/40 text-xs sm:text-sm">Pass Rate</div>
                  <div className="text-white text-xl sm:text-2xl lg:text-3xl font-black">Live</div>
                </div>
              </div>
            </div>

            {/* Floating badges - responsive positioning */}
            <motion.div 
              animate={{ y: [0,-5,0] }} 
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -bottom-4 sm:-bottom-6 right-0 sm:-right-5 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-gray-200"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-bold text-slate-900">Fee Auto-Collected</div>
                  <div className="text-[10px] sm:text-xs text-slate-500">Recurring · Invoice sent</div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0,5,0] }} 
              transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              className="absolute -top-4 sm:-top-5 left-0 sm:-left-4 bg-purple-600 rounded-xl sm:rounded-2xl p-2 sm:p-4 shadow-xl"
            >
              <div className="text-white text-sm sm:text-base lg:text-lg font-black">All-in-One</div>
              <div className="text-white/70 text-[8px] sm:text-xs font-semibold">Academy Platform</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Text */}
        <motion.div 
          initial={{ opacity: 0, x: 30 }} 
          whileInView={{ opacity: 1, x: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.65 }}
          className="order-1 lg:order-2"
        >
          <Badge icon={Sparkles} label="OUR STORY" />
          <Heading level={2} className="mb-4 sm:mb-5">
            We built what educators actually needed.
          </Heading>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
            In 2021, our team spent time with academies, training providers, and independent educators around the world. The same frustrations surfaced everywhere: courses scattered across tools, manual fee collection, certificates issued one-by-one, and no real visibility into student progress.
          </p>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6 sm:mb-8">
            Generic LMS platforms were either too rigid, too expensive, or built for universities — not for the lean, growing academies trying to deliver real results. So we built Krumago Academy: purpose-designed for educators from day one.
          </p>

          <div className="space-y-3 sm:space-y-4">
            {[
              "Purpose-built for educators, not adapted from generic LMS",
              "Replaces 5+ disconnected tools with one platform",
              "From your first student to thousands — scales with you",
              "Academies go live in under a day",
            ].map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: -16 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-2 sm:gap-3"
              >
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base font-medium text-slate-800">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Container>
  </section>
);

/* ══════════════════════════
   PROBLEM / SOLUTION (responsive)
══════════════════════════ */
const ProblemSolution = () => {
  const problems = [
    "Courses scattered across multiple tools",
    "Manual student enrollment and tracking",
    "No automated fee collection or reminders",
    "No centralized gradebook or attendance",
    "Zero visibility into learner progress",
    "Certificates issued manually one-by-one",
  ];
  
  const solutions = [
    "Unified course builder & content library",
    "Automated enrollment, onboarding & profiles",
    "Recurring billing, invoices & payment reminders",
    "Live gradebook, attendance & assignment tracking",
    "Per-student analytics & completion dashboards",
    "Auto-issued certificates on course completion",
  ];
  
  const benefits = [
    { icon: GraduationCap, title: "Course Delivery", desc: "Build and publish structured courses with video, documents, quizzes and live sessions." },
    { icon: Users,         title: "Student Lifecycle", desc: "From first enrollment to graduation — every student touchpoint in one place." },
    { icon: BookOpen,      title: "Assessments & Certs", desc: "Quizzes, exams, assignments and auto-issued certifications at scale." },
  ];

  return (
    <section className="bg-slate-50 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <Container>
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <Badge icon={Zap} label="THE ACADEMY PROBLEM" />
          <Heading level={2}>
            Built specifically for <span style={{ color: P }}>Educators</span>
          </Heading>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto mt-3 sm:mt-4 leading-relaxed">
            Unlike generic LMS tools, Krumago Academy is purpose-built for educators and training providers.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Without */}
          <div className="bg-white border border-red-200 rounded-xl sm:rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-slate-900">Without Krumago Academy</h3>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {problems.map((p, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-3">
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-300 flex-shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-slate-600">{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* With */}
          <div className="bg-white border border-green-200 rounded-xl sm:rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-slate-900">With Krumago Academy</h3>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {solutions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base font-medium text-slate-800">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <Grid cols={{ default: 1, md: 3 }} gap={6}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center hover:translate-y-[-4px] hover:shadow-xl hover:border-purple-200 transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-5" style={{ color: P }}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="font-extrabold text-base sm:text-lg text-slate-900 mb-2 sm:mb-3">{b.title}</div>
                <div className="text-xs sm:text-sm text-slate-600 leading-relaxed">{b.desc}</div>
              </motion.div>
            );
          })}
        </Grid>
      </Container>
    </section>
  );
};

/* ══════════════════════════
   FEATURES (responsive)
══════════════════════════ */
const features = [
  { icon: BookOpen,      title: "Course Builder",          desc: "Create structured courses with modules, video lessons, PDFs and quizzes. Publish in minutes with a clean drag-and-drop builder.", color: P },
  { icon: Users,         title: "Student Management CRM",  desc: "Enroll students, track progress, manage cohorts and communicate — all from one unified student profile.", color: "#7c3aed" },
  { icon: CreditCard,    title: "Automated Billing",        desc: "One-time or recurring fee plans, auto-generated invoices, and payment reminders without any manual effort.", color: "#059669" },
  { icon: Video,         title: "Live Class Scheduling",    desc: "Schedule live sessions, send calendar invites, manage attendance and record sessions for replay.", color: "#db2777" },
  { icon: BarChart3,     title: "Learning Analytics",       desc: "Track completion rates, quiz scores, engagement metrics and course revenue across your entire institution.", color: P },
  { icon: Bell,          title: "Automated Notifications",  desc: "Enrollment confirmations, assignment reminders, exam alerts and fee reminders — fully automated.", color: "#0891b2" },
  { icon: Award,         title: "Certifications & Exams",   desc: "Create timed exams, auto-grade responses and issue branded certificates automatically upon completion.", color: "#059669" },
  { icon: GraduationCap, title: "Multi-Instructor Portal",  desc: "Assign courses to multiple instructors, manage permissions and give each instructor their own analytics view.", color: "#7c3aed" },
];

const Features = () => (
  <section className="bg-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
    <Container>
      <div className="text-center mb-8 sm:mb-12 lg:mb-16">
        <Badge icon={Zap} label="PLATFORM FEATURES" />
        <Heading level={2}>
          Everything to run your <span style={{ color: P }}>Academy</span>
        </Heading>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto mt-3 sm:mt-4 leading-relaxed">
          Purpose-built tools for educators and training providers — from your first student to scaling across locations.
        </p>
      </div>

      <Grid cols={{ default: 1, sm: 2, lg: 4 }} gap={6}>
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: i * 0.07 }}
              className="bg-slate-50 border border-gray-200 rounded-xl p-6 hover:bg-white hover:border-purple-300 hover:shadow-xl hover:translate-y-[-4px] transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4" style={{ color: f.color }}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="font-extrabold text-base sm:text-lg text-slate-900 mb-2 sm:mb-3">{f.title}</div>
              <div className="text-xs sm:text-sm text-slate-600 leading-relaxed">{f.desc}</div>
            </motion.div>
          );
        })}
      </Grid>
    </Container>
  </section>
);

/* ══════════════════════════
   HOW IT WORKS (responsive)
══════════════════════════ */
const steps = [
  {
    step: "01", icon: Settings, color: P, bg: "rgba(98,92,160,0.08)",
    title: "Build Your Courses",
    desc: "Create structured courses with modules, video lessons, quizzes and assignments. Set pricing, enrolment limits and access windows in minutes.",
    points: ["Upload lessons, videos & resources", "Define course pricing & plans", "Set enrolment rules & schedules"],
  },
  {
    step: "02", icon: Users, color: "#7c3aed", bg: "rgba(124,58,237,0.07)",
    title: "Enroll Your Students",
    desc: "Share your portal link. Students self-enroll, pay online and get instant access. You get a full CRM view of every student's activity and progress.",
    points: ["Students self-enroll & pay online", "Automated welcome & onboarding", "Progress tracked from day one"],
  },
  {
    step: "03", icon: TrendingUp, color: "#059669", bg: "rgba(5,150,105,0.07)",
    title: "Automate & Scale",
    desc: "Billing, reminders, grading and certificates all run on autopilot. Use analytics to improve course quality and grow to more students and instructors.",
    points: ["Automated billing & reminders", "Auto-issued certificates", "Scale instructors & locations"],
  },
];

const HowItWorks = () => (
  <section className="bg-slate-50 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
    <Container>
      <div className="text-center mb-8 sm:mb-12 lg:mb-16">
        <Badge icon={TrendingUp} label="HOW IT WORKS" />
        <Heading level={2}>
          Up and running in <span style={{ color: P }}>3 simple steps</span>
        </Heading>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto mt-3 sm:mt-4 leading-relaxed">
          From course creation to full automation — launch your academy on Krumago in less than a day.
        </p>
      </div>

      <Grid cols={{ default: 1, md: 3 }} gap={6}>
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: i * 0.15 }}
              className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 relative hover:translate-y-[-5px] hover:shadow-xl hover:border-purple-200 transition-all duration-300"
            >
              <div className="absolute -top-3 left-4 sm:left-6 w-8 h-8 bg-white border-2 rounded-lg flex items-center justify-center" style={{ borderColor: s.color }}>
                <span className="text-[10px] sm:text-xs font-black" style={{ color: s.color }}>{s.step}</span>
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-4 sm:mb-5 mt-4" style={{ background: s.bg, color: s.color }}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-3 sm:mb-4">{s.title}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4 sm:mb-6">{s.desc}</p>
              <div className="space-y-2 sm:space-y-3">
                {s.points.map((p, pi) => (
                  <div key={pi} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-slate-700 font-medium">{p}</span>
                  </div>
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl" style={{ background: s.color }} />
            </motion.div>
          );
        })}
      </Grid>
    </Container>
  </section>
);

/* ══════════════════════════
   PLATFORM PILLARS (responsive)
══════════════════════════ */
const Pillars = () => {
  const pillars = [
    { icon: BookOpen,      color: P,         title: "Course Management",   desc: "Build, publish and manage structured courses with full content control and scheduling." },
    { icon: CreditCard,    color: "#059669",  title: "Billing Automation",  desc: "Recurring fees, smart invoicing, and payment reminders that run without manual effort." },
    { icon: Award,         color: "#7c3aed",  title: "Certs & Assessments", desc: "Timed exams, auto-grading, and branded certificates issued automatically on completion." },
    { icon: BarChart3,     color: "#0891b2",  title: "Learning Analytics",  desc: "Completion rates, quiz scores, and engagement data to improve every course." },
  ];
  
  return (
    <section className="bg-[#1e1535] py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <Container>
        <Grid cols={{ default: 1, sm: 2, lg: 4 }} gap={6}>
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 sm:p-8 lg:p-10 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl mx-auto mb-4 sm:mb-5 flex items-center justify-center" style={{ background: `${p.color}22` }}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: p.color }} />
                </div>
                <div className="text-base sm:text-lg font-extrabold text-white mb-2 sm:mb-3">{p.title}</div>
                <div className="text-xs sm:text-sm text-white/40 leading-relaxed">{p.desc}</div>
              </motion.div>
            );
          })}
        </Grid>
      </Container>
    </section>
  );
};

/* ══════════════════════════
   VALUES (responsive)
══════════════════════════ */
const values = [
  { icon: Target,        color: P,         title: "Educator-First",     desc: "Every feature is built for real teaching workflows — not adapted from generic enterprise tools." },
  { icon: Zap,           color: "#d97706",  title: "Speed to Value",     desc: "Launch your academy in hours, not months. No IT team, no complex configuration required." },
  { icon: Heart,         color: "#db2777",  title: "Genuine Partnership", desc: "We treat every educator as a partner. Your feedback directly shapes the product roadmap." },
  { icon: Shield,        color: "#059669",  title: "Reliable & Secure",  desc: "Enterprise-grade security, uptime SLA, and data compliance baked in from day one." },
];

const Values = () => (
  <section className="bg-slate-50 py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
    <Container>
      <div className="text-center mb-8 sm:mb-12 lg:mb-16">
        <Badge icon={Award} label="OUR VALUES" />
        <Heading level={2}>
          Why educators <span style={{ color: P }}>choose us</span>
        </Heading>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto mt-3 sm:mt-4 leading-relaxed">
          We're not just software — we're a team that genuinely cares about your academy's success.
        </p>
      </div>

      <Grid cols={{ default: 1, sm: 2, lg: 4 }} gap={6}>
        {values.map((v, i) => {
          const Icon = v.icon;
          return (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: i * 0.1 }}
              className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl mx-auto mb-4 sm:mb-5 flex items-center justify-center" style={{ background: `${v.color}12` }}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: v.color }} />
              </div>
              <div className="font-extrabold text-base sm:text-lg text-slate-900 mb-2 sm:mb-3">{v.title}</div>
              <div className="text-xs sm:text-sm text-slate-600 leading-relaxed">{v.desc}</div>
            </motion.div>
          );
        })}
      </Grid>
    </Container>
  </section>
);

/* ══════════════════════════
   CTA (responsive)
══════════════════════════ */
const CTA = () => (
  <section className="bg-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
    <Container>
      <div className="relative bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center overflow-hidden">
        {/* Background patterns */}
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.06) 1px, transparent 0)", backgroundSize: "24px 24px" }} />

        <div className="relative">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          
          <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold text-white mb-4 sm:mb-6">
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" /> BUILT FOR EDUCATORS
          </div>
          
          <Heading level={2} className="text-white mb-4 sm:mb-5">
            The smarter way to run<br />your academy
          </Heading>
          
          <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed">
            Deliver great learning experiences, automate admin, and grow your student base — all from one platform.
          </p>

          {/* Feature highlights - responsive grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 max-w-3xl mx-auto mb-6 sm:mb-8">
            {[
              { icon: Zap, label: "Launch in Under an Hour" },
              { icon: Shield, label: "Enterprise Security" },
              { icon: TrendingUp, label: "Scales Any Size" },
              { icon: Target, label: "Full Analytics" },
            ].map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="bg-white/10 rounded-lg p-3 sm:p-4 text-center">
                  <div className="flex justify-center mb-1 sm:mb-2 text-white/90">
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <div className="text-[9px] sm:text-xs text-white font-semibold leading-tight">{h.label}</div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8">
            <motion.button 
              whileHover={{ scale: 1.02, y: -2 }} 
              whileTap={{ scale: 0.98 }}
              className="bg-white text-purple-700 border-none px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold cursor-pointer inline-flex items-center justify-center gap-2 hover:shadow-xl transition-all"
            >
              Get Started Free <ArrowRight className="w-4 h-4" />
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.02, y: -2 }} 
              whileTap={{ scale: 0.98 }}
              className="bg-transparent text-white border-2 border-white/40 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold cursor-pointer inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
            >
              <Mail className="w-4 h-4" /> Request Demo
            </motion.button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center">
            {[
              { dot: "#4ade80", text: "Start for free" },
              { dot: "rgba(255,255,255,0.7)", text: "Explore all features for 14 days" },
              { dot: "rgba(255,255,255,0.5)", text: "Easy to get started" },
            ].map((t, i) => (
              <div key={i} className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: t.dot }} />
                <span className="text-xs sm:text-sm text-white/60">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ══════════════════════════
   ROOT - AcademyAbout
══════════════════════════ */
const AcademyAbout = () => (
  <div className="overflow-x-hidden">
    <Hero />
    <Marquee />
    <Mission />
    <ProblemSolution />
    <Features />
    <HowItWorks />
    <Pillars />
    {/* <Testimonials /> */}
    <Values />
    <CTA />
  </div>
);

export default AcademyAbout;
